import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface StatementLoadingDialogData {
    title?: string;
    message?: string;
    estimatedDurationMs?: number;
}

@Component({
    selector: 'app-statement-loading-dialog',
    templateUrl: './statement-loading-dialog.component.html',
    styleUrls: ['./statement-loading-dialog.component.scss'],
    standalone: false
})
export class StatementLoadingDialogComponent implements OnInit, OnDestroy {
    title: string;
    message: string;
    progress = 0;
    private readonly fastPhasePercent = 70;
    private readonly fastPhaseDurationMs = 8000;
    private readonly softCapPercent = 85; // soft cap and soft range should always equal 99%
    private readonly softCapRange = 14;
    private readonly softCapHalfLifeMs = 30000;
    private readonly estimatedDurationMs: number;
    private readonly minimumDisplayMs = 1500;
    private intervalId?: ReturnType<typeof setInterval>;
    private animationFrameId?: number;
    private startTime = Date.now();

    constructor(@Inject(MAT_DIALOG_DATA) public data: StatementLoadingDialogData, private changeDetectorRef: ChangeDetectorRef) {
        this.title = data?.title ?? 'Uploading Statement';
        this.message = data?.message ?? 'This may take a few minutes for larger or scanned documents.';
        this.estimatedDurationMs = data?.estimatedDurationMs ?? 45000;
    }

    ngOnInit(): void {
        this.updateProgress();
        this.intervalId = setInterval(() => this.updateProgress(), 300);
    }

    private updateProgress(): void {
        const elapsed = Date.now() - this.startTime;

        if (elapsed < this.fastPhaseDurationMs) {
            // Fast climb to 70% over the first several seconds, so even a
            // very quick upload shows real, visible motion right away.
            const t = elapsed / this.fastPhaseDurationMs;
            this.progress = this.fastPhasePercent * t;
        } else if (elapsed < this.estimatedDurationMs) {
            // Slow climb from 70% to 90% across the rest of the estimated
            // duration, so typical uploads settle into a believable pace.
            const remainingRange = this.softCapPercent - this.fastPhasePercent;
            const remainingDuration = this.estimatedDurationMs - this.fastPhaseDurationMs;
            const t = (elapsed - this.fastPhaseDurationMs) / remainingDuration;
            this.progress = this.fastPhasePercent + remainingRange * Math.min(1, t);
        } else {
            // Past the estimate: asymptotically approach 99%, so there's
            // always some visible movement no matter how long the wait
            // runs — never a truly static plateau.
            const overflowMs = elapsed - this.estimatedDurationMs;
            const creep = this.softCapRange * (1 - Math.exp(-overflowMs / this.softCapHalfLifeMs));
            this.progress = this.softCapPercent + creep;
        }
        this.changeDetectorRef.markForCheck();
    }

    complete(): Promise<void> {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        const elapsed = Date.now() - this.startTime;
        const remainingMs = Math.max(300, this.minimumDisplayMs - elapsed);
        return this.animateToComplete(remainingMs);
    }

    private animateToComplete(durationMs: number): Promise<void> {
        return new Promise((resolve) => {
            const startProgress = this.progress;
            const startTime = Date.now();
            const step = () => {
                const t = Math.min(1, (Date.now() - startTime) / durationMs);
                this.progress = startProgress + (100 - startProgress) * t;
                this.changeDetectorRef.markForCheck();
                if (t < 1) {
                    this.animationFrameId = requestAnimationFrame(step);
                } else {
                    resolve();
                }
            };
            step();
        });
    }

    ngOnDestroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }
}